import type { ColumnDef } from '@tanstack/react-table';

import { booleanColumn, dateColumn, multiSelectColumn, textColumn } from 'components/DataTable';
import { type GetTicketsQuery } from 'data/__generated__';

import { TicketStatusTag } from '../components/TicketStatusTag';
import { CRITICALITY_LABELS, PRIORITY_LABELS } from '../helpers';

export type TicketRow = GetTicketsQuery['tickets'][number];

export type TicketsFilter = {
    searchValue?: string;
    status?: string[];
    stage?: string[];
    team?: string[];
    ticketType?: string[];
    priority?: string[];
    criticality?: string[];
    active?: boolean;
};

const PRIORITY_OPTIONS = [
    { value: '0', label: PRIORITY_LABELS['0'] },
    { value: '1', label: PRIORITY_LABELS['1'] },
    { value: '2', label: PRIORITY_LABELS['2'] },
    { value: '3', label: PRIORITY_LABELS['3'] },
];

const CRITICALITY_OPTIONS = [
    { value: 'normal', label: CRITICALITY_LABELS.normal },
    { value: 'high', label: CRITICALITY_LABELS.high },
    { value: 'block', label: CRITICALITY_LABELS.block },
];

const STATUS_OPTIONS = [
    { value: 'open', label: 'Ouvert' },
    { value: 'closed', label: 'Terminé' },
];

function uniqueOptions(values: Array<string | null | undefined>): { value: string; label: string }[] {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];

    for (const value of values) {
        if (!value || seen.has(value)) {
            continue;
        }
        seen.add(value);
        options.push({ value, label: value });
    }

    return options.sort((optionA, optionB) => optionA.label.localeCompare(optionB.label, 'fr'));
}

export const getTicketsColumns = (tickets: TicketRow[]): ColumnDef<TicketRow, unknown>[] => {
    const stageOptions = uniqueOptions(tickets.map((ticket) => ticket.stage?.name));
    const teamOptions = uniqueOptions(tickets.map((ticket) => ticket.team?.name));
    const ticketTypeOptions = uniqueOptions(tickets.map((ticket) => ticket.ticketType?.name));

    return [
        textColumn<TicketRow, TicketsFilter>({
            accessorFn: (row) => row.odooRef ?? String(row.odooId),
            id: 'odooRef',
            header: 'Référence',
            defaultVisible: true,
            truncate: false,
        }),
        textColumn<TicketRow, TicketsFilter>({
            accessorKey: 'name',
            header: 'Intitulé',
            defaultVisible: true,
        }),
        multiSelectColumn<TicketRow, TicketsFilter>({
            accessorFn: (row) => (row.isClosed ? 'closed' : 'open'),
            id: 'status',
            header: 'Statut',
            filterKey: 'status',
            defaultVisible: true,
            options: STATUS_OPTIONS,
            cell: ({ row }) => <TicketStatusTag isClosed={row.original.isClosed} />,
        }),
        multiSelectColumn<TicketRow, TicketsFilter>({
            accessorFn: (row) => row.stage?.name ?? '',
            id: 'stage',
            header: 'Étape',
            filterKey: 'stage',
            defaultVisible: true,
            options: stageOptions,
            cell: ({ row }) => row.original.stage?.name ?? '—',
        }),
        multiSelectColumn<TicketRow, TicketsFilter>({
            accessorFn: (row) => row.team?.name ?? '',
            id: 'team',
            header: 'Équipe',
            filterKey: 'team',
            defaultVisible: true,
            options: teamOptions,
            cell: ({ row }) => row.original.team?.name ?? '—',
        }),
        multiSelectColumn<TicketRow, TicketsFilter>({
            accessorFn: (row) => row.ticketType?.name ?? '',
            id: 'ticketType',
            header: 'Type',
            filterKey: 'ticketType',
            defaultVisible: true,
            options: ticketTypeOptions,
            cell: ({ row }) => row.original.ticketType?.name ?? '—',
        }),
        multiSelectColumn<TicketRow, TicketsFilter>({
            accessorKey: 'priority',
            header: 'Priorité',
            filterKey: 'priority',
            defaultVisible: true,
            options: PRIORITY_OPTIONS,
            cell: ({ row }) => PRIORITY_LABELS[row.original.priority] ?? row.original.priority,
        }),
        multiSelectColumn<TicketRow, TicketsFilter>({
            accessorFn: (row) => row.ticketCriticality ?? '',
            id: 'ticketCriticality',
            header: 'Criticité',
            filterKey: 'criticality',
            defaultVisible: true,
            options: CRITICALITY_OPTIONS,
            cell: ({ row }) =>
                row.original.ticketCriticality
                    ? (CRITICALITY_LABELS[row.original.ticketCriticality] ?? row.original.ticketCriticality)
                    : '—',
        }),
        textColumn<TicketRow, TicketsFilter>({
            accessorFn: (row) => row.microstoreColumn?.siteName ?? row.site?.name ?? '',
            id: 'site',
            header: 'Site',
            defaultVisible: true,
            cell: ({ row }) => row.original.microstoreColumn?.siteName ?? row.original.site?.name ?? '—',
        }),
        textColumn<TicketRow, TicketsFilter>({
            accessorFn: (row) => row.microstoreColumn?.name ?? row.machine?.name ?? '',
            id: 'machine',
            header: 'Machine',
            defaultVisible: true,
            cell: ({ row }) => row.original.microstoreColumn?.name ?? row.original.machine?.name ?? '—',
        }),
        textColumn<TicketRow, TicketsFilter>({
            accessorFn: (row) => row.operator?.fullname ?? '',
            id: 'operator',
            header: 'Opérateur Totem',
            defaultVisible: false,
            cell: ({ row }) => row.original.operator?.fullname ?? '—',
        }),
        booleanColumn<TicketRow, TicketsFilter>({
            accessorKey: 'active',
            header: 'Actif',
            filterKey: 'active',
            defaultVisible: false,
        }),
        dateColumn<TicketRow, TicketsFilter>({
            accessorKey: 'odooCreateDate',
            header: 'Créé (Odoo)',
            defaultVisible: true,
        }),
        dateColumn<TicketRow, TicketsFilter>({
            accessorKey: 'createdAt',
            header: 'Arrivé dans Totem',
            defaultVisible: false,
        }),
    ];
};
